import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { X, Play, Pause, SkipForward, RotateCcw, Volume2, VolumeX, Subtitles, Loader2, Share2 } from "lucide-react";
import { api, USER_ID, track } from "./api";
import { useSound } from "./sound";
import { StampSeal } from "./StampSeal";
import { shareStamp } from "./shareStamp";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ASSETS = {
  "butter-chicken": {
    backdrop: "/story/bc-delhi.jpg",
    reveal: "Murgh Makhani",
    ingredients: [
      { src: "/story/ing-tandoori.png", label: "Tandoori chicken" },
      { src: "/story/ing-tomatoes.png", label: "Ripe tomatoes" },
      { src: "/story/ing-cream.png", label: "Fresh cream" },
      { src: "/story/ing-butter.png", label: "Golden butter" },
    ],
    timeline: [
      { year: "1920s", text: "Tandoori chicken perfected in Peshawar" },
      { year: "1947", text: "Moti Mahal, Delhi — Butter Chicken is born" },
      { year: "TODAY", text: "Urban fires it in the bhatti — the VeerGi way" },
    ],
  },
  "dal-makhani": {
    backdrop: "/story/dm-delhi.jpg",
    reveal: "Dal Makhani",
    ingredients: [
      { src: "/story/ing-lentils.png", label: "Black lentils" },
      { src: "/story/ing-tomatoes.png", label: "Ripe tomatoes" },
      { src: "/story/ing-butter.png", label: "Golden butter" },
      { src: "/story/ing-cream.png", label: "Fresh cream" },
    ],
    timeline: [
      { year: "1950s", text: "Moti Mahal simmers the first Dal Makhani" },
      { year: "24 HRS", text: "The overnight simmer that builds its depth" },
      { year: "TODAY", text: "Urban cooks it the old way — low and slow" },
    ],
  },
  "tandoori-chicken": {
    backdrop: "/story/tc-peshawar.jpg",
    reveal: "Tandoori Chicken",
    ingredients: [
      { src: "/story/ing-yogurt.png", label: "Thick yogurt" },
      { src: "/story/ing-chilies.png", label: "Kashmiri chilies" },
      { src: "/story/ing-lemon.png", label: "Fresh lemon" },
      { src: "/story/ing-tandoori.png", label: "Into the tandoor" },
    ],
    timeline: [
      { year: "1920s", text: "Kundan Lal Gujral perfects it in Peshawar" },
      { year: "1947", text: "Partition carries the recipe to Delhi's Moti Mahal" },
      { year: "TODAY", text: "Urban's bhatti fires it the classic way" },
    ],
  },
  "chicken-dum-biryani": {
    backdrop: "/story/by-lucknow.jpg",
    reveal: "Dum Biryani",
    ingredients: [
      { src: "/story/ing-rice.png", label: "Basmati rice" },
      { src: "/story/ing-saffron.png", label: "Saffron" },
      { src: "/story/ing-spices.png", label: "Whole spices" },
      { src: "/story/ing-yogurt.png", label: "Yogurt marinade" },
    ],
    timeline: [
      { year: "1784", text: "The Nawab's sealed pots feed Lucknow through famine" },
      { year: "DUM", text: "'To breathe' — steam alone cooks the sealed deg" },
      { year: "TODAY", text: "Urban seals every pot the Awadhi way" },
    ],
  },
  "sarson-ka-saag": {
    backdrop: "/story/sk-punjab.jpg",
    reveal: "Sarson Ka Saag",
    ingredients: [
      { src: "/story/ing-greens.png", label: "Mustard greens" },
      { src: "/story/ing-spinach.png", label: "Winter spinach" },
      { src: "/story/ing-roti.png", label: "Makki di roti" },
      { src: "/story/ing-makhan.png", label: "White makhan" },
    ],
    timeline: [
      { year: "SARSON", text: "Mustard fields bloom gold across Punjab" },
      { year: "OVERNIGHT", text: "Slow-simmered, rested — richer the next day" },
      { year: "TODAY", text: "Urban serves it with makki di roti & makhan" },
    ],
  },
};

const WalkFigure = ({ className }) => (
  <div className={`story-walkin absolute bottom-4 -left-20 w-16 ${className || ""}`} aria-hidden="true">
    <div className="rig-walk-bob relative w-full" style={{ aspectRatio: "227 / 480" }}>
      <img src="/mascot/rig/walk_torso.png" alt="" draggable="false" className="absolute inset-0 w-full h-full" />
      <img src="/mascot/rig/walk_legb.png" alt="" draggable="false" className="absolute inset-0 w-full h-full rig-leg-back" style={{ transformOrigin: "31.7% 83.3%" }} />
      <img src="/mascot/rig/walk_legf.png" alt="" draggable="false" className="absolute inset-0 w-full h-full rig-leg-front" style={{ transformOrigin: "74% 83.3%" }} />
      <img src="/mascot/rig/walk_head.png" alt="" draggable="false" className="absolute inset-0 w-full h-full rig-head-step" style={{ transformOrigin: "55% 18.3%" }} />
    </div>
  </div>
);

const Confetti = () => (
  <div className="story-confetti absolute inset-0 z-20 pointer-events-none" aria-hidden="true">
    {Array.from({ length: 24 }).map((_, i) => (
      <span key={i} style={{ left: `${(i * 37 + 11) % 100}%`, animationDelay: `${(i % 8) * 0.12}s`,
        background: ["#D4AF37", "#F0C742", "#E25822", "#FFFFFF"][i % 4] }} />
    ))}
  </div>
);

const Stage = ({ visual, scene, dish, assets }) => {
  if (visual === "stamp") {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-[#0C0A07]">
        <div className="absolute inset-0 opacity-25" style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(212,175,55,0.25), transparent 55%)" }} />
        <div className="w-52 h-52 md:w-64 md:h-64 story-stamp story-stamp-land">
          <StampSeal place={scene.stamp_place} year={scene.stamp_year} />
        </div>
        <WalkFigure />
      </div>
    );
  }
  if (visual === "delhi") {
    return (
      <div className="absolute inset-0 overflow-hidden bg-black">
        <img src={assets.backdrop} alt="Old Delhi kitchen, illustrated" className="story-backdrop absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: "linear-gradient(to top, rgba(0,0,0,0.75) 0%, transparent 45%)" }} />
        <img src="/mascot/point.png" alt="" className="rig-sway absolute bottom-2 left-3 h-44 md:h-52" draggable="false" />
      </div>
    );
  }
  if (visual === "ingredients") {
    return (
      <div className="absolute inset-0 overflow-hidden bg-black">
        <img src={assets.backdrop} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-40 h-40 md:w-48 md:h-48 rounded-full overflow-hidden border-2 border-[#D4AF37]/70 story-glow">
            <img src={dish?.image} alt={dish?.name} className="w-full h-full object-cover" />
          </div>
        </div>
        {assets.ingredients.map((ing, i) => (
          <div key={ing.label} className="story-pop absolute flex flex-col items-center gap-1"
            style={{ animationDelay: `${1 + i * 1.7}s`,
              ...( [{ top: "7%", left: "6%" }, { top: "7%", right: "6%" }, { bottom: "9%", left: "6%" }, { bottom: "9%", right: "6%" }][i] ) }}>
            <img src={ing.src} alt={ing.label} className="h-20 md:h-24 drop-shadow-[0_6px_16px_rgba(0,0,0,0.6)]" draggable="false" />
            <span className="text-[9px] font-bold tracking-[0.2em] text-[#D4AF37] bg-black/70 rounded-full px-2.5 py-1">{ing.label.toUpperCase()}</span>
          </div>
        ))}
      </div>
    );
  }
  if (visual === "dish") {
    return (
      <div className="absolute inset-0 flex items-center justify-center bg-[#0C0A07]">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: "radial-gradient(circle at 50% 45%, rgba(226,88,34,0.4), transparent 60%)" }} />
        <div className="relative">
          <div className="relative w-56 h-56 md:w-64 md:h-64 rounded-full overflow-hidden border-[3px] border-[#D4AF37] story-glow">
            <img src={dish?.image} alt={dish?.name} className="w-full h-full object-cover" />
          </div>
          <span className="story-steam absolute -top-6 left-1/3 w-8 h-12 rounded-full bg-white/50 blur-md" />
          <span className="story-steam absolute -top-8 left-1/2 w-6 h-14 rounded-full bg-white/40 blur-md" style={{ animationDelay: "1.2s" }} />
          <p className="story-rise absolute -bottom-10 inset-x-0 text-center font-display text-2xl font-bold text-[#D4AF37]" style={{ animationDelay: "0.8s" }}>
            {assets.reveal || dish?.name}
          </p>
        </div>
        <img src="/mascot/thumbsup.png" alt="" className="rig-nod absolute bottom-2 right-2 h-36 md:h-44" draggable="false" />
      </div>
    );
  }
  if (visual === "timeline") {
    return (
      <div className="absolute inset-0 bg-[#0C0A07] flex flex-col justify-center px-6 gap-4">
        <p className="text-[10px] tracking-[0.35em] text-[#D4AF37] font-bold">A LEGEND'S TIMELINE</p>
        {assets.timeline.map((tnode, i) => (
          <div key={tnode.year} className="story-rise flex items-start gap-4" style={{ animationDelay: `${0.5 + i * 1.6}s` }}>
            <span className="font-display text-2xl font-black text-[#D4AF37] w-20 shrink-0">{tnode.year}</span>
            <div className="flex-1 border-l-2 border-[#D4AF37]/40 pl-4 pb-1">
              <p className="text-sm text-white/85 leading-snug">{tnode.text}</p>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center bg-[#0C0A07] gap-4">
      <img src="/mascot/celebrate.png" alt="" className="rig-bounce h-52 md:h-60" draggable="false" />
      <p className="story-rise font-display text-2xl font-bold text-center px-8">Let's see if you were paying attention…</p>
    </div>
  );
};

export default function StoryScene({ slug, scene, dish, onClose }) {
  const { soundOn } = useSound();
  const assets = ASSETS[slug] || { backdrop: dish?.image, ingredients: [], timeline: [] };
  const [seg, setSeg] = useState(0);
  const [phase, setPhase] = useState("playing");
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(false);
  const [subsOn, setSubsOn] = useState(true);
  const [loadingVoice, setLoadingVoice] = useState(false);
  const [picked, setPicked] = useState(null);
  const [result, setResult] = useState(null);
  const audioRef = useRef(null);
  const sharedRef = useRef(null);
  const timerRef = useRef(null);
  const endAtRef = useRef(0);
  const remainRef = useRef(0);
  const mutedRef = useRef(false);
  const aliveRef = useRef(true);

  // one reusable element, unlocked during the opening tap — iPad Safari blocks fresh Audio() outside a gesture
  const ensureAudio = () => {
    if (!sharedRef.current) sharedRef.current = new Audio();
    const a = sharedRef.current;
    const v = window.__xpNarrVol;
    a.volume = typeof v === "number" ? v : 1;
    return a;
  };

  const stopAll = () => {
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.pause();
    }
    audioRef.current = null;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  };

  const startTimer = useCallback((ms, next) => {
    endAtRef.current = Date.now() + ms;
    timerRef.current = setTimeout(next, ms);
  }, []);

  const runSeg = useCallback(async (i) => {
    if (!aliveRef.current) return;
    stopAll();
    setPaused(false);
    if (i >= scene.segments.length) {
      setPhase("quiz");
      return;
    }
    setSeg(i);
    const s = scene.segments[i];
    const next = () => runSeg(i + 1);
    if (soundOn && !mutedRef.current) {
      setLoadingVoice(true);
      try {
        const r = await api.post(`/story-scene/${slug}/narrate`, { user_id: USER_ID, segment_id: s.id });
        setLoadingVoice(false);
        if (!aliveRef.current || mutedRef.current) return startTimer(s.dur * 1000, next);
        if (r.data.audio_url) {
          const a = ensureAudio();
          a.onended = next;
          a.src = `${BACKEND_URL}${r.data.audio_url}`;
          audioRef.current = a;
          await a.play();
          return;
        }
      } catch {
        setLoadingVoice(false);
      }
    }
    startTimer(s.dur * 1000, next);
  }, [scene, slug, soundOn, startTimer]);

  useEffect(() => {
    aliveRef.current = true;
    document.body.style.overflow = "hidden";
    if (soundOn) {
      // silent priming play inside the opening tap's activation window unlocks the element for all later segments
      const a = ensureAudio();
      if (!a.src) {
        a.src = "data:audio/wav;base64,UklGRiQAAABXQVZFZm10IBAAAAABAAEAIlYAAESsAAACABAAZGF0YQAAAAA=";
        a.play().catch(() => {});
      }
    }
    [assets.backdrop, dish?.image, ...assets.ingredients.map((x) => x.src)].filter(Boolean).forEach((src) => { const im = new Image(); im.src = src; });
    track("story_scene_started", { slug });
    const id = setTimeout(() => runSeg(0), 350);
    return () => {
      aliveRef.current = false;
      clearTimeout(id);
      document.body.style.overflow = "";
      if (sharedRef.current) {
        sharedRef.current.onended = null;
        sharedRef.current.pause();
      }
      clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const togglePause = () => {
    if (phase !== "playing") return;
    if (!paused) {
      if (audioRef.current) audioRef.current.pause();
      else {
        clearTimeout(timerRef.current);
        remainRef.current = Math.max(400, endAtRef.current - Date.now());
      }
      setPaused(true);
    } else {
      if (audioRef.current) audioRef.current.play().catch(() => {});
      else startTimer(remainRef.current, () => runSeg(seg + 1));
      setPaused(false);
    }
  };

  const toggleMute = () => {
    const m = !mutedRef.current;
    mutedRef.current = m;
    setMuted(m);
    if (m && audioRef.current) {
      const a = audioRef.current;
      const left = isFinite(a.duration) ? Math.max(500, (a.duration - a.currentTime) * 1000) : scene.segments[seg].dur * 1000;
      a.onended = null;
      a.pause();
      audioRef.current = null;
      if (!paused) startTimer(left, () => runSeg(seg + 1));
      else remainRef.current = left;
    }
  };

  const skip = () => {
    if (phase === "playing") runSeg(seg + 1);
  };

  const replay = () => {
    setResult(null);
    setPicked(null);
    setPhase("playing");
    track("story_scene_replayed", { slug });
    runSeg(0);
  };

  const speakLine = async (kind) => {
    if (!soundOn || mutedRef.current) return;
    try {
      const r = await api.post(`/story-scene/${slug}/narrate`, { user_id: USER_ID, segment_id: kind });
      if (r.data.audio_url) {
        const a = ensureAudio();
        a.onended = null;
        a.src = `${BACKEND_URL}${r.data.audio_url}`;
        audioRef.current = a;
        a.play().catch(() => {});
      }
    } catch {}
  };

  const answer = async (i) => {
    if (picked !== null) return;
    setPicked(i);
    try {
      const r = await api.post(`/story-scene/${slug}/answer`, { user_id: USER_ID, answer: i, table: localStorage.getItem("xp_table") || "" });
      setResult(r.data);
      setPhase("result");
      speakLine(r.data.correct ? "correct" : "wrong");
      api.post(`/dishes/${slug}/story-complete`, { user_id: USER_ID }).catch(() => {});
      if (r.data.correct) window.dispatchEvent(new CustomEvent("xp-mascot", { detail: { type: "quiz-correct" } }));
    } catch {
      setPicked(null);
    }
  };

  const close = () => {
    stopAll();
    track("story_scene_closed", { slug, phase });
    onClose();
  };

  const current = scene.segments[Math.min(seg, scene.segments.length - 1)];
  const q = scene.question;

  return (
    <div data-testid="story-scene" className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-sm flex justify-center theme-dark">
      <div className="w-full max-w-md md:max-w-lg h-full flex flex-col relative text-left">
        <div className="flex items-center justify-between px-4 pt-4 pb-2 z-10">
          <div>
            <p className="text-[9px] tracking-[0.35em] text-[#D4AF37] font-bold">URBAN PASSPORT · STORY</p>
            <p className="font-display text-lg font-bold leading-tight">{scene.title}</p>
          </div>
          <button data-testid="story-close" onClick={close} aria-label="Close story" className="xp-iconbtn shrink-0">
            <X size={18} />
          </button>
        </div>

        {phase === "playing" && (
          <div className="flex gap-1.5 px-4 pb-2 z-10" data-testid="story-progress">
            {scene.segments.map((s, i) => (
              <span key={s.id} className={`h-1 flex-1 rounded-full ${i < seg ? "bg-[#D4AF37]" : i === seg ? "bg-[#D4AF37]/60" : "bg-white/15"}`} />
            ))}
          </div>
        )}

        <div className="flex-1 relative overflow-hidden rounded-2xl mx-3 border border-white/10">
          {phase === "playing" && <Stage visual={current.visual} scene={scene} dish={dish} assets={assets} />}

          {phase === "quiz" && (
            <div className="absolute inset-0 bg-[#0C0A07] flex flex-col justify-center px-5 gap-3" data-testid="story-quiz">
              <p className="text-[10px] tracking-[0.35em] text-[#D4AF37] font-bold">THE CHALLENGE · +{scene.points} / −5 URBAN POINTS</p>
              <p className="font-display text-xl font-bold leading-snug">{q.text}</p>
              <div className="space-y-2 mt-1">
                {q.options.map((o, i) => (
                  <button key={i} data-testid={`story-quiz-option-${i}`} onClick={() => answer(i)} disabled={picked !== null}
                    className={`w-full text-left rounded-xl border px-4 py-3 text-sm transition-colors ${
                      picked === i ? "border-[#D4AF37] bg-[#D4AF37]/15" : "border-white/15 bg-[#141414] hover:border-[#D4AF37]/50"}`}>
                    {o}
                  </button>
                ))}
              </div>
              {scene.already_earned && <p className="text-[10px] text-white/40">You've already earned these points — answer for the glory!</p>}
            </div>
          )}

          {phase === "result" && result && (
            <div className="absolute inset-0 bg-[#0C0A07] flex flex-col items-center justify-center px-6 text-center gap-3" data-testid="story-result">
              {result.correct && <Confetti />}
              {result.correct ? (
                <>
                  <div className="w-40 h-40 story-stamp story-stamp-land"><StampSeal place={scene.stamp_place} year={scene.stamp_year} /></div>
                  <p className="font-display text-2xl font-bold text-[#D4AF37]">Shabash! Stamp collected</p>
                  <p className="text-sm text-white/70">
                    {result.awarded ? `+${result.points} URBAN POINTS earned`
                      : result.month_locked ? "This story already counted this month — the stamp is still yours."
                      : result.capped ? "You've hit today's points cap — the stamp is yours, points refill tomorrow."
                      : "You'd already earned these points — legend status confirmed."}
                  </p>
                  {result.set_complete && (
                    <div data-testid="passport-complete" className="story-rise w-full rounded-2xl border border-[#D4AF37] bg-[#D4AF37]/10 px-4 py-3 text-center">
                      <p className="text-[10px] tracking-[0.3em] text-[#D4AF37] font-black">PASSPORT COMPLETE!</p>
                      <p className="text-xs text-white/85 mt-1">
                        {result.bonus?.awarded
                          ? `Every story stamp collected — +${result.bonus.points} BONUS URBAN POINTS!`
                          : "Every story stamp collected — you're a certified Urban legend."}
                      </p>
                    </div>
                  )}
                  <button data-testid="story-share" onClick={() => shareStamp({ place: scene.stamp_place, year: scene.stamp_year, dishName: assets.reveal || dish?.name || scene.title })}
                    className="flex items-center gap-1.5 border border-[#D4AF37]/50 text-[#D4AF37] font-bold rounded-full px-6 py-2.5 text-[10px] tracking-widest">
                    <Share2 size={12} /> SHARE YOUR STAMP
                  </button>
                </>
              ) : (
                <>
                  <img src="/mascot/thumbsup.png" alt="" className="rig-nod h-40" draggable="false" />
                  <p className="font-display text-2xl font-bold">Ooh, so close!</p>
                  <p className="text-sm text-white/70">{q.options[result.correct_index]} — now you know the secret.</p>
                  {result.penalty > 0 && <p className="text-xs text-[#E25822]" data-testid="story-penalty">−{result.penalty} URBAN POINTS — win them back with your next right answer.</p>}
                </>
              )}
              <button data-testid="story-continue" onClick={() => setPhase("end")}
                className="mt-2 bg-[#D4AF37] hover:bg-[#F0C742] text-black font-bold rounded-full px-8 py-3 text-xs tracking-widest">
                CONTINUE
              </button>
            </div>
          )}

          {phase === "end" && (
            <div data-testid="story-end" className="absolute inset-0 bg-[#0C0A07] flex flex-col items-center justify-center px-6 text-center gap-4">
              <StampSeal place={scene.stamp_place} year={scene.stamp_year} className="w-24 h-24" />
              <p className="font-display text-xl font-bold">You've met {assets.reveal || dish?.name || "the dish"}</p>
              {scene.recommend_dish && (
                <Link to={`/experience/dish/${scene.recommend_dish.slug}`} onClick={close} data-testid="story-recommend"
                  className="w-full rounded-2xl border border-[#D4AF37]/40 bg-[#141414] p-3 flex items-center gap-3 text-left">
                  {scene.recommend_dish.image && <img src={scene.recommend_dish.image} alt="" className="w-14 h-14 rounded-xl object-cover" />}
                  <span className="flex-1 min-w-0">
                    <span className="block text-[9px] tracking-[0.3em] text-[#D4AF37] font-bold">HUNGRY FOR MORE? TRY THIS NEXT</span>
                    <span className="block text-sm font-bold truncate mt-0.5">{scene.recommend_dish.name}</span>
                  </span>
                </Link>
              )}
              <div className="flex gap-2">
                <button data-testid="story-replay-end" onClick={replay}
                  className="border border-white/25 text-white/80 font-bold rounded-full px-6 py-3 text-xs tracking-widest flex items-center gap-1.5">
                  <RotateCcw size={13} /> REPLAY
                </button>
                <button data-testid="story-done" onClick={close}
                  className="bg-[#D4AF37] hover:bg-[#F0C742] text-black font-bold rounded-full px-8 py-3 text-xs tracking-widest">
                  DONE
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 pt-2 min-h-[54px] flex items-center justify-center">
          {subsOn && phase === "playing" && (
            <p key={seg} data-testid="story-subtitle" className="story-sub text-center text-sm text-white/90 leading-snug max-w-[95%]">
              {current.text}
            </p>
          )}
        </div>

        <div className="flex items-center justify-center gap-2 px-4 pb-5 pt-1">
          <button data-testid="story-pause" onClick={togglePause} aria-label={paused ? "Play" : "Pause"} disabled={phase !== "playing"}
            className="xp-iconbtn disabled:opacity-30">
            {loadingVoice ? <Loader2 size={16} className="animate-spin" /> : paused ? <Play size={16} /> : <Pause size={16} />}
          </button>
          <button data-testid="story-skip" onClick={skip} aria-label="Skip" disabled={phase !== "playing"} className="xp-iconbtn disabled:opacity-30">
            <SkipForward size={16} />
          </button>
          <button data-testid="story-replay" onClick={replay} aria-label="Replay" className="xp-iconbtn">
            <RotateCcw size={15} />
          </button>
          <button data-testid="story-mute" onClick={toggleMute} aria-label={muted ? "Unmute narration" : "Mute narration"}
            className={`xp-iconbtn ${muted ? "!border-[#E25822]/60 !text-[#E25822]" : ""}`}>
            {muted ? <VolumeX size={16} /> : <Volume2 size={16} />}
          </button>
          <button data-testid="story-subs" onClick={() => setSubsOn((v) => !v)} aria-label="Toggle subtitles"
            className={`xp-iconbtn ${subsOn ? "!border-[#D4AF37]/60 !text-[#D4AF37]" : ""}`}>
            <Subtitles size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
